import React, {useEffect, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ToolTipCell} from 'components/ToolTipCell';
import api from 'libs/api';
import {ExitModal} from '../../../Modals';
import {useTracking} from '../../../../services/analytics/TrackingContext';

const absoluteLink = /^https?:\/\//i;

/**
 * Renders the documents table column. Each `column.meta.documents` entry may include
 * `unavailableText` — hover tooltip when the document is missing or the source is not a valid
 * http(s) link (and `showOnlyFound` is false).
 */
export const DocumentsCell = ({settings, styleSheet, value, column, rowData}) => {
  const {trackResultClick, trackExternalLinkExitModal} = useTracking();
  const [showExitModal, setShowExitModal] = useState(false);
  const [activeDocument, setActiveDocument] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  // Flipped by `handleHide` (and unmount) so an in-flight document-link resolution that the user
  // has already backed out of never reaches `window.open`. Not state: the pending
  // `handleOpenDocument` closure has to see the new value without a re-render.
  const cancelledRef = useRef(false);

  useEffect(() => () => {
    cancelledRef.current = true;
  }, []);

  const handleDocumentClick = (e, doc) => {
    e.preventDefault();
    setActiveDocument(doc);
    setShowExitModal(true);
    const clickType = e.currentTarget.dataset.trackClickType;
    if (clickType && rowData) {
      trackResultClick(clickType, rowData);
    }
  };

  /**
   * `document.source` points at a payer/third-party host and may be dead by now, so resolve it
   * through the documentLink lambda first — it hands back either the original url or a short-lived
   * Policyr Magic Link. Any failure falls back to the raw source, which is today's behaviour.
   */
  const handleOpenDocument = async () => {
    if (isResolving || !activeDocument) {
      return;
    }
    cancelledRef.current = false;
    setIsResolving(true);

    let resolvedUrl = activeDocument.source;
    try {
      const {data} = await api.post('/document-link', {
        url: activeDocument.source,
        assetRevisionId: activeDocument.assetRevisionId,
      });
      resolvedUrl = data?.response || activeDocument.source;
    } catch (error) {
      console.error('Error resolving document link:', error);
    }

    // The reject/close/esc/backdrop paths stay enabled while we resolve, so the user may well have
    // declined by now. `resolvedUrl` and `activeDocument` are closure locals that `handleHide`'s
    // state resets cannot reach, so without this the tab still opens — on the `catch` path at the
    // raw third-party url this resolution exists to avoid. `handleHide` already reset the state.
    if (cancelledRef.current) {
      return;
    }

    // Tracked here rather than on click: firing it up front counted a 'leaves' for attempts that
    // were abandoned mid-resolution, double-counting them against `handleHide`'s 'stays'.
    trackExternalLinkExitModal('leaves');
    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
    setIsResolving(false);
    setShowExitModal(false);
    setActiveDocument(null);
  };

  const handleHide = () => {
    cancelledRef.current = true;
    trackExternalLinkExitModal('stays');
    setShowExitModal(false);
    setActiveDocument(null);
    setIsResolving(false);
  };

  const defaultDocumentMeta = {
    name: 'Coverage Document',
    label: 'Coverage Document',
    showOnlyFound: false,
    unavailableText: 'Coverage document not available',
    icon: {
      prefix: 'far',
      name: 'file',
    },
  };

  // Track documents that have already been assigned to prevent duplicates
  const usedDocuments = new Set();

  return (
    <>
      <div>{
        column.meta.documents.map((documentMeta, key) => {
          //Find document by matching document type name in the documentMeta object
          let document = value.find((doc) => {
            return !usedDocuments.has(doc) && doc.documentTypes.some((type) => type.name === documentMeta.name);
          });

          //If not found, find primary document
          if (!document && documentMeta.name === defaultDocumentMeta.name) {
            document = value.find((doc) => {
              return !usedDocuments.has(doc) && doc.isPrimary === true;
            });
          }

          // Exclude documents whose types are in excludeDocuments list
          if (document && documentMeta.excludeDocuments?.length > 0 && document.documentTypes) {
            const isExcluded = document.documentTypes.some((type) =>
              documentMeta.excludeDocuments.includes(type.name)
            );
            if (isExcluded) {
              document = null;
            }
          }

          //If primary document is not found and showOnlyFound is true, return null
          if (!document && documentMeta.showOnlyFound) {
            return null;
          }

          // Mark document as used to prevent duplicate assignments
          if (document) {
            usedDocuments.add(document);
          }

          // Merge defaultDocumentMeta only if documentMeta is missing properties
          if (!documentMeta.label) {
            documentMeta = {...defaultDocumentMeta, ...documentMeta};
          }

          // If no icon is specified, use default
          if (!documentMeta.icon) {
            documentMeta.icon = defaultDocumentMeta.icon;
          }

          const icon = findIconDefinition({prefix: documentMeta.icon.prefix, iconName: documentMeta.icon.name});
          // No `href` on purpose: it only leaked the raw third-party url into the browser status bar
          // and the right-click menu, and let ctrl/middle-click skip both the interstitial and the
          // documentLink resolution. `className` is required so bootstrap's
          // `a:not([href]):not([class])` reboot rule doesn't strip the link colour and underline.
          return (
            <React.Fragment key={`${key}-${documentMeta.label}`}>
              {document && document.documentTypes.length > 0 && absoluteLink.test(document.source) ? (
                <a
                  className='document-link'
                  onClick={(e) => handleDocumentClick(e, document)}
                  onKeyDown={(e) => ((e.key === 'Enter' || e.key === ' ') && handleDocumentClick(e, document))}
                  role='button'
                  tabIndex={0}
                  aria-haspopup='dialog'
                  data-track-click-type={documentMeta.name === 'Coverage Document' ? 'coverage_document' : documentMeta.name === 'Prior Authorization (PA) Form' || documentMeta.name === 'PA Form' ? 'pa_form' : 'coverage_document'}
                  {...(column.meta?.contextAriaLabel && rowData && {
                    'aria-label': `${documentMeta.label} for ${rowData.payer} ${rowData.planType}`
                  })}
                >
                  <FontAwesomeIcon icon={icon}/>{documentMeta.label}
                </a>
              ) : (
                !documentMeta.showOnlyFound ? (
                  <ToolTipCell
                    placement={'bottom'}
                    styleSheet={styleSheet}
                    text={documentMeta.unavailableText}
                    placeholder={(<span><FontAwesomeIcon icon={icon} />{documentMeta.label}</span>)}
                    id={`doc-tooltip-${key}`}
                  />
                ) : (
                  <span></span>
                )
              )}
              <br/>
            </React.Fragment>
          );
        })
      }</div>
      <ExitModal
        settings={settings}
        styleSheet={styleSheet}
        showExitModal={showExitModal}
        handleHide={handleHide}
        handleOpenDocument={handleOpenDocument}
        isResolving={isResolving}
      />
    </>
  );
};

